import { useEffect, useRef, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatDistanceToNowStrict } from "date-fns";
import { Send, Image as ImageIcon, X, Lock, Unlock } from "lucide-react";
import MessageBody from "@/components/MessageBody";
import InitialsAvatar from "@/components/InitialsAvatar";

const POLL_MS = 5000;

export default function ChatPanel({ conversations, activeId, onSelectConversation, onConversationsChanged, viewerRole, loading = false }) {
  const active = conversations.find((c) => c.id === activeId) || null;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8" data-testid="chat-panel">
      {/* Conversation list */}
      <aside className="lg:col-span-4 rounded-2xl border border-mindora-line bg-mindora-panel overflow-hidden" data-testid="chat-list">
        <div className="text-[11px] uppercase tracking-[0.2em] text-mindora-ink/70 px-4 py-3 border-b border-mindora-line">
          Conversations ({conversations.length})
        </div>
        {loading ? (
          <div className="p-4 space-y-3" data-testid="chat-list-skeleton">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center gap-3 animate-pulse">
                <div className="w-10 h-10 rounded-full bg-mindora-line/50" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3 bg-mindora-line/50 rounded w-2/3" />
                  <div className="h-2.5 bg-mindora-line/40 rounded w-5/6" />
                </div>
              </div>
            ))}
          </div>
        ) : conversations.length === 0 ? (
          <div className="p-6 text-sm text-mindora-muted italic">
            {viewerRole === "user"
              ? "Start a chat from a counsellor's page — look for the 'Message [name]' button."
              : "No clients have reached out yet."}
          </div>
        ) : (
          <ul className="max-h-[600px] overflow-y-auto divide-y divide-mindora-line/60">
            {conversations.map((c) => (
              <li key={c.id}>
                <button
                  data-testid={`chat-conv-${c.id}`}
                  onClick={() => onSelectConversation(c.id)}
                  className={`w-full text-left p-4 hover:bg-mindora-bg transition-colors ${activeId === c.id ? "bg-mindora-bg" : ""}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden shrink-0">
                      <InitialsAvatar
                        name={viewerRole === "user" ? c.therapist_name : c.user_name}
                        className="w-full h-full"
                        size={40}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-2">
                        <div className="font-serif text-base text-mindora-ink truncate">
                          {viewerRole === "user" ? c.therapist_name : c.user_name}
                        </div>
                        {c.last_message_at && (
                          <div className="text-[10px] text-mindora-muted whitespace-nowrap">
                            {formatDistanceToNowStrict(new Date(c.last_message_at), { addSuffix: false })}
                          </div>
                        )}
                      </div>
                      <div className="text-xs text-mindora-muted truncate mt-0.5">
                        {c.last_message_preview || "—"}
                      </div>
                    </div>
                    {c.unread_count > 0 && (
                      <span data-testid={`chat-conv-${c.id}-unread`} className="shrink-0 ml-2 bg-mindora-red text-mindora-bg text-[10px] rounded-full px-2 py-0.5 font-sans tracking-wider">
                        {c.unread_count}
                      </span>
                    )}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </aside>

      {/* Active conversation */}
      <section className="lg:col-span-8 rounded-2xl border border-mindora-line bg-mindora-bg min-h-[560px] flex flex-col" data-testid="chat-conversation">
        {active ? (
          <ConversationView
            conv={active}
            viewerRole={viewerRole}
            onChanged={onConversationsChanged}
          />
        ) : (
          <div className="m-auto text-mindora-muted italic">
            Pick a conversation on the left, or start one from a counsellor's page.
          </div>
        )}
      </section>
    </div>
  );
}

function ConversationView({ conv, viewerRole, onChanged }) {
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [pendingImage, setPendingImage] = useState(null); // {id, url, name}
  const [sending, setSending] = useState(false);
  const [busyImg, setBusyImg] = useState(false);
  const [blocked, setBlocked] = useState(conv.blocked_by_therapist);
  const scrollerRef = useRef(null);
  const lastFetchAt = useRef(null);

  useEffect(() => { setBlocked(conv.blocked_by_therapist); }, [conv.id, conv.blocked_by_therapist]);

  const loadInitial = useCallback(async () => {
    try {
      const { data } = await api.get(`/conversations/${conv.id}/messages`);
      setMessages(data);
      if (data.length > 0) lastFetchAt.current = data[data.length - 1].created_at;
      onChanged?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load messages");
    }
  }, [conv.id, onChanged]);

  // Reset and refetch when conversation changes
  useEffect(() => {
    setMessages([]);
    lastFetchAt.current = null;
    loadInitial();
  }, [conv.id, loadInitial]);

  // Polling (5s) for new messages
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const after = lastFetchAt.current;
        const url = after ? `/conversations/${conv.id}/messages?after=${encodeURIComponent(after)}` : `/conversations/${conv.id}/messages`;
        const { data } = await api.get(url);
        if (data.length > 0) {
          setMessages((prev) => {
            const seen = new Set(prev.map((m) => m.id));
            const next = [...prev, ...data.filter((m) => !seen.has(m.id))];
            return next;
          });
          lastFetchAt.current = data[data.length - 1].created_at;
          onChanged?.();
        }
      } catch {
        // ignore transient errors
      }
    }, POLL_MS);
    return () => clearInterval(interval);
  }, [conv.id, onChanged]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages.length]);

  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking same file
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB");
      return;
    }
    setBusyImg(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/chat-images", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPendingImage({ id: data.id, url: data.url, name: file.name });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Image upload failed");
    } finally {
      setBusyImg(false);
    }
  };

  const send = async () => {
    if (!body.trim() && !pendingImage) return;
    setSending(true);
    try {
      const { data } = await api.post(`/conversations/${conv.id}/messages`, {
        body: body.trim() || undefined,
        image_id: pendingImage?.id,
      });
      setMessages((prev) => [...prev, data]);
      lastFetchAt.current = data.created_at;
      setBody("");
      setPendingImage(null);
      onChanged?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send");
    } finally {
      setSending(false);
    }
  };

  const toggleBlock = async () => {
    try {
      const { data } = await api.post(`/conversations/${conv.id}/block`);
      setBlocked(data.blocked_by_therapist);
      toast.success(data.blocked_by_therapist ? "Paused — the client can't send new messages." : "Reopened — the client can message you again.");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update");
    }
  };

  const otherName = viewerRole === "user" ? conv.therapist_name : conv.user_name;
  const showBlocked = viewerRole === "user" && blocked;

  return (
    <div className="flex flex-col h-[600px]">
      {/* Header */}
      <div className="px-5 py-3 border-b border-mindora-line flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full overflow-hidden">
            <InitialsAvatar name={otherName} className="w-full h-full" size={36} />
          </div>
          <div>
            <div className="font-serif text-lg text-mindora-ink leading-none" data-testid="chat-header-name">{otherName}</div>
            {viewerRole === "therapist" && (
              <div className="text-[10px] uppercase tracking-wider text-mindora-muted mt-1">Client</div>
            )}
          </div>
        </div>
        {viewerRole === "therapist" && (
          <button
            onClick={toggleBlock}
            data-testid="chat-block-toggle"
            className={`text-[10px] uppercase tracking-[0.18em] rounded-full px-3 py-1.5 border ${
              blocked
                ? "bg-mindora-red text-mindora-bg border-mindora-red"
                : "border-mindora-line text-mindora-ink/70 hover:text-mindora-red hover:border-mindora-red"
            }`}
          >
            {blocked ? (<><Unlock size={11} className="inline mr-1" />Unblock</>) : (<><Lock size={11} className="inline mr-1" />Pause client</>)}
          </button>
        )}
      </div>

      {/* Messages */}
      <div ref={scrollerRef} className="flex-1 overflow-y-auto px-5 py-5 space-y-3" data-testid="chat-messages">
        {messages.length === 0 ? (
          <div className="text-sm text-mindora-muted italic text-center mt-10">
            Say hi — this is the start of your conversation.
          </div>
        ) : (
          messages.map((m) => (
            <MessageBubble key={m.id} m={m} mine={(viewerRole === "user" && m.sender_role === "user") || (viewerRole === "therapist" && m.sender_role === "therapist")} />
          ))
        )}
      </div>

      {/* Composer */}
      {showBlocked ? (
        <div className="px-5 py-4 border-t border-mindora-line text-sm text-mindora-muted italic" data-testid="chat-blocked-banner">
          The counsellor has paused this conversation. You can't send new messages right now.
        </div>
      ) : (
        <div className="px-5 py-4 border-t border-mindora-line" data-testid="chat-composer">
          {pendingImage && (
            <div className="flex items-center gap-3 mb-2 text-xs text-mindora-muted">
              <img src={pendingImage.url} alt={pendingImage.name} className="w-12 h-12 object-cover rounded-md border border-mindora-line" />
              <span className="truncate flex-1">{pendingImage.name}</span>
              <button onClick={() => setPendingImage(null)} className="text-mindora-red hover:opacity-80" aria-label="Remove image">
                <X size={14} />
              </button>
            </div>
          )}
          <div className="flex items-end gap-2">
            <label className="cursor-pointer text-mindora-ink/70 hover:text-mindora-red p-2" data-testid="chat-image-picker">
              <ImageIcon size={18} />
              <input type="file" accept="image/*" onChange={onPickImage} className="hidden" disabled={busyImg} />
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              rows={2}
              placeholder="Type a message… ⏎ to send, Shift+⏎ for a new line"
              data-testid="chat-input"
              className="flex-1 resize-none rounded-xl border border-mindora-line bg-mindora-bg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-mindora-red"
            />
            <Button
              onClick={send}
              disabled={sending || (!body.trim() && !pendingImage)}
              data-testid="chat-send"
              className="btn-primary rounded-full h-11 px-5"
            >
              <Send size={14} className="mr-1.5" />
              Send
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function MessageBubble({ m, mine }) {
  return (
    <div
      className={`flex ${mine ? "justify-end" : "justify-start"}`}
      data-testid={`chat-msg-${m.id}`}
      data-mine={mine}
    >
      <div
        className={`max-w-[78%] rounded-2xl px-4 py-2.5 leading-relaxed text-sm shadow-sm ${
          mine
            ? "bg-mindora-red text-mindora-bg rounded-br-sm"
            : "bg-mindora-panel border border-mindora-line text-mindora-ink rounded-bl-sm"
        }`}
      >
        {m.image_url && (
          <img
            src={m.image_url}
            alt="attachment"
            className="rounded-lg mb-1.5 max-h-[260px] object-cover"
          />
        )}
        {m.body && (
          <div className="whitespace-pre-wrap break-words">
            <MessageBody text={m.body} />
          </div>
        )}
        <div className={`text-[10px] mt-1 tracking-wide ${mine ? "text-mindora-bg/70" : "text-mindora-muted"}`}>
          {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </div>
      </div>
    </div>
  );
}
